import React, { useEffect, useRef, useState } from 'react';
import { auth, signInWithGoogle, logOut } from '../firebase';
import { onAuthStateChanged, User } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from './ui/dropdown-menu';
import { LogIn, LogOut, User as UserIcon, Key, Bug, ImagePlus, Loader2, X, HelpCircle, Check } from 'lucide-react';
import { useStore } from '../store';
import { ALLOWED_LOGO_TYPES, MAX_LOGO_BYTES, isAllowedLogoFile, uploadLogo } from '../services/reportThemeService';
import { VERSION_LABEL } from '../lib/appVersion';
import { AccentPalettePicker } from './AccentPalettePicker';
import { setKeepSignedInOnDevice } from '../lib/authDevicePreference';
import { useKeepSignedInOnDevice } from '../lib/useKeepSignedInOnDevice';
import {
  shouldShowExplainerHints,
  setExplainerHintsPref,
  notifyExplainerHintsChanged,
} from '../lib/explainerHintsPref';

export function UserMenu() {
  const [user, setUser] = useState<User | null>(null);
  const keepSignedIn = useKeepSignedInOnDevice(user?.uid);
  const toggleKeepSignedIn = () => {
    if (!user || auth.currentUser?.uid !== user.uid) return;
    try {
      setKeepSignedInOnDevice(user.uid, !keepSignedIn);
      toast.success(keepSignedIn ? 'Automatic sign-out after 30 minutes is restored.' : 'Automatic idle sign-out is off for your account on this device.');
    } catch {
      toast.error('This browser could not save the sign-in preference. Your previous setting is unchanged.');
    }
  };
  const navigate = useNavigate();
  const userRole = useStore((s) => s.userRole); // R-130-2
  const isAdmin = userRole === 'admin'; // R-130-2
  // Brand logo (feat/org-logo) — the user's PERSONAL logo overrides the
  // org logo for them. Uploaded via the SAME GCS-REST helper as the
  // template-studio brand uploader (no AbortSignal regression).
  const userLogoUrl = useStore((s) => s.userLogoUrl);
  const setUserLogo = useStore((s) => s.setUserLogo);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  // "Show help hints" — global toggle for the inline explainer/help cards
  // (ContextualGuideTip). Reflects the resolved pref (explicit choice, or the
  // first-5-logins default). Flipping it persists an explicit choice and
  // dispatches the change event so any visible hint cards update live.
  const [hintsOn, setHintsOn] = useState(shouldShowExplainerHints);
  const toggleHints = () => {
    const next = !hintsOn;
    setHintsOn(next);
    setExplainerHintsPref(next);
    notifyExplainerHintsChanged();
  };

  const handleLogoFile = async (file: File | undefined) => {
    if (!file || !user) return;
    if (!isAllowedLogoFile(file)) {
      toast.error(`Unsupported file type "${file.type || 'unknown'}". Use PNG, SVG, JPEG, or WebP.`);
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      toast.error(`Logo too large (${(file.size / 1024).toFixed(0)} KB). Max 1 MB.`);
      return;
    }
    setUploadingLogo(true);
    try {
      // User scope — personal brand logo at logos/{uid}/<id>.<ext>.
      const url = await uploadLogo(file, 'user', user.uid);
      await setUserLogo(url);
      toast.success('Logo updated');
    } catch (e) {
      const raw = e instanceof Error ? e.message : 'Upload failed';
      // Stale-bundle defence — mirror the template-studio uploader.
      toast.error(/AbortSignal/i.test(raw)
        ? 'Upload failed — please retry (a stale tab may need a refresh).'
        : raw);
    } finally {
      setUploadingLogo(false);
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  };

  const handleClearLogo = async () => {
    try {
      await setUserLogo(null);
      toast.success('Logo removed');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not remove logo');
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        import('../store').then(({ useStore }) => {
          useStore.getState().fetchRagReports();
        });
      }
    });
    return () => unsubscribe();
  }, []);

  if (!user) {
    return (
      <Button 
        variant="outline" 
        size="sm" 
        onClick={signInWithGoogle}
        className="h-8 rounded-md border-border-subtle bg-inset text-text-primary hover:bg-state-hover uppercase tracking-widest text-[10px] font-bold"
      >
        <LogIn className="w-3.5 h-3.5 mr-1.5" /> Sign In
      </Button>
    );
  }

  return (
    <>
    {/* Hidden file input for the personal-logo upload affordance (kept
        outside the dropdown so it survives the menu close on item click). */}
    <input
      ref={logoInputRef}
      type="file"
      accept={Array.from(ALLOWED_LOGO_TYPES).join(',')}
      className="hidden"
      onChange={(e) => void handleLogoFile(e.target.files?.[0] ?? undefined)}
      data-testid="user-menu-logo-input"
    />
    <DropdownMenu>
      <DropdownMenuTrigger
        className="h-8 rounded-md border border-border-subtle bg-inset text-text-primary hover:bg-state-hover uppercase tracking-widest text-[10px] font-bold flex items-center gap-2 px-2 sm:px-3 inline-flex justify-center whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50"
      >
        {user.photoURL ? (
          <img src={user.photoURL} alt="Avatar" className="w-4 h-4 rounded-full" referrerPolicy="no-referrer" />
        ) : (
          <UserIcon className="w-3.5 h-3.5" />
        )}
        {/* Name is heavy on a phone header — show avatar-only below sm. */}
        <span className="hidden sm:inline">{user.displayName || 'User'}</span>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48 rounded-lg border-border bg-surface-1 shadow-card font-mono">
        <div className="px-2 py-1.5 text-xs text-text-secondary truncate">
          {user.email}
        </div>
        <DropdownMenuSeparator className="bg-state-active" />
        {/* App accent palette (v1.3.4) — 5-swatch APP-UI accent picker.
            APP CHROME only (buttons/links/active chips/focus rings) — NOT
            the report ReportTheme/cover branding. Instant + persisted via
            <html data-accent>. Wrapped so the dropdown does not close on
            swatch click (mousedown preventDefault keeps the menu open). */}
        <div
          onMouseDown={(e) => e.preventDefault()}
          onClick={(e) => e.stopPropagation()}
        >
          <AccentPalettePicker />
        </div>
        <DropdownMenuSeparator className="bg-state-active" />
        {/* Show help hints — global show/hide for the inline explainer cards
            (ContextualGuideTip). preventDefault keeps the menu open on click so
            the user can see the tick flip; the change event updates any
            visible cards live. */}
        <DropdownMenuItem
          onClick={(e) => { e.preventDefault(); toggleHints(); }}
          className="rounded-md hover:bg-state-active focus:bg-state-active text-text-primary uppercase text-xs tracking-wider cursor-pointer flex items-center"
          data-testid="user-menu-toggle-hints"
          role="menuitemcheckbox"
          aria-checked={hintsOn}
        >
          <HelpCircle className="w-4 h-4 mr-2" />
          <span className="flex-1">Show help hints</span>
          {hintsOn && <Check className="w-4 h-4 ml-2 text-primary" />}
        </DropdownMenuItem>
        <DropdownMenuSeparator className="bg-state-active" />
        {isAdmin && ( // R-130-2
          <>
            <DropdownMenuItem
              onClick={() => window.dispatchEvent(new CustomEvent('open-api-keys-settings'))}
              className="rounded-md hover:bg-state-active focus:bg-state-active text-text-primary uppercase text-xs tracking-wider cursor-pointer"
            >
              <Key className="w-4 h-4 mr-2" />
              API Keys
            </DropdownMenuItem>
            <DropdownMenuSeparator className="bg-state-active" />
          </>
        )}
        {/* Brand logo (feat/org-logo) — upload a PERSONAL logo that overrides
            the org logo + Datacrate default for this user, across the
            top-menu wordmark AND the report cover/template default. */}
        <DropdownMenuItem
          onClick={(e) => { e.preventDefault(); if (!uploadingLogo) logoInputRef.current?.click(); }}
          className="rounded-md hover:bg-state-active focus:bg-state-active text-text-primary uppercase text-xs tracking-wider cursor-pointer"
          data-testid="user-menu-upload-logo"
        >
          {uploadingLogo
            ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            : <ImagePlus className="w-4 h-4 mr-2" />}
          {userLogoUrl ? 'Replace logo' : 'Upload logo'}
        </DropdownMenuItem>
        {userLogoUrl && (
          <DropdownMenuItem
            onClick={(e) => { e.preventDefault(); void handleClearLogo(); }}
            className="rounded-md hover:bg-state-active focus:bg-state-active text-text-secondary uppercase text-xs tracking-wider cursor-pointer"
            data-testid="user-menu-remove-logo"
          >
            <X className="w-4 h-4 mr-2" />
            Remove logo
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator className="bg-state-active" />
        <DropdownMenuItem
          onClick={() => navigate('/issues')}
          className="rounded-md hover:bg-state-active focus:bg-state-active text-text-primary uppercase text-xs tracking-wider cursor-pointer"
        >
          <Bug className="w-4 h-4 mr-2" />
          Your issues
        </DropdownMenuItem>
        <DropdownMenuSeparator className="bg-state-active" />
        <DropdownMenuItem
          onClick={(e) => { e.preventDefault(); toggleKeepSignedIn(); }}
          role="menuitemcheckbox"
          aria-checked={keepSignedIn}
          data-testid="user-menu-keep-signed-in"
          className="rounded-md hover:bg-state-active focus:bg-state-active text-text-primary text-xs cursor-pointer flex items-center"
        >
          <span className="flex-1 whitespace-normal">Keep me signed in on this device</span>
          {keepSignedIn && <Check className="w-4 h-4 ml-2 shrink-0 text-primary" />}
        </DropdownMenuItem>
        <p className="px-2 py-1 text-[10px] text-text-secondary whitespace-normal">For private devices. Account security checks still apply.</p>
        <DropdownMenuSeparator className="bg-state-active" />
        <DropdownMenuItem onClick={logOut} className="rounded-md hover:bg-state-active focus:bg-state-active text-text-primary uppercase text-xs tracking-wider cursor-pointer">
          <LogOut className="w-4 h-4 mr-2" />
          Sign Out
        </DropdownMenuItem>
        {/* Release stamp — bottom of the menu (moved out of the top bar).
            Single source of truth: src/lib/appVersion. */}
        <DropdownMenuSeparator className="bg-state-active" />
        <div
          className="px-2 py-1.5 text-[10px] font-mono lowercase tracking-wider text-text-muted/70 select-text"
          title="App release · build"
          data-testid="user-menu-version"
        >
          {VERSION_LABEL}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
    </>
  );
}
